import React, { useMemo } from 'react'
import { ChargingCardModel } from '../../api/models/charging-card-model'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { DatePicker, TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import * as z from 'zod'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EditChargingCardPayload, useEditChargingCard } from '../../api/use-edit-charing-card'
import { makeStyles } from 'tss-react/mui'
import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { useHagerBridgePermissions } from '../../../shared/hooks/use-hager-bridge-permissions'
import { CardStatus } from '../../../shared'
import { getChargingCardLabel } from '../../utils/get-charging-card-label'
import { useFetchUsersByInstallation } from '../../api/use-fetch-users-by-installation'
import { isPast, isValid as isValidDate } from '@e3dc-react/shell/Libs/date'
import moment from 'moment'
import { Trans } from 'react-i18next'

const useStyles = makeStyles()(theme => ({
  radioButtons: {
    flexDirection: 'row',
  },
  infoText: {
    fontSize: theme.spacing(2),
  },
  selectInputWrapper: {
    backgroundColor: theme.palette.shades.shade04,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  title: {
    fontWeight: 500,
    marginBottom: theme.spacing(3),
    color: theme.palette.shades.shade01,
  },
}))

const CARD_NAME_MAX_CHAR = 255
const DESCRIPTION_MAX_CHAR = 255

export interface EditChargingCardDialogProps {
  open: boolean
  onClose: () => void
  place: number
  chargingCard: ChargingCardModel
}

export const EditChargingCardDialog: React.FC<EditChargingCardDialogProps> = ({ open, onClose, place, chargingCard }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { users } = useFetchUsersByInstallation(Number(chargingCard.installationId))
  const { editChargingCard, isLoading } = useEditChargingCard(onClose)
  const { permissions } = useHagerBridgePermissions()

  const validationSchema = z.object({
    status: z.nativeEnum(CardStatus),
    cardName: z.string().max(CARD_NAME_MAX_CHAR, t('emobility.charging-card-name.nameToLong')).nullable(),
    description: z.string().max(DESCRIPTION_MAX_CHAR, t('emobility.chargingCards.description-to-long')).nullable(),
    userId: z.string().nullable(),
    expirationDate: z
      .string()
      .nullable()
      .refine(value => value === null || isValidDate(new Date(value)), {
        message: t('emobility.chargingCards.valid-date-format'),
      }),
  })

  type EditChargingCardFormData = z.infer<typeof validationSchema>

  const {
    control,
    formState: { isDirty, isValid, errors },
    getValues,
    trigger,
    setValue,
  } = useForm<EditChargingCardFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      status: chargingCard.status,
      cardName: chargingCard.cardName,
      description: chargingCard.description,
      userId: chargingCard.userId,
      expirationDate: chargingCard.expirationDate,
    },
    mode: 'onChange',
  })

  const handleSubmit = (): void => {
    const formValues = getValues()

    const payload: EditChargingCardPayload = {
      name: permissions.chargingCardPermissions.editChargingCard && formValues.cardName ? formValues.cardName : undefined,
      description: permissions.chargingCardPermissions.editChargingCard ? formValues.description : undefined,
      card_status: permissions.chargingCardPermissions.editChargingCardStatus ? formValues.status : undefined,
      user_id: permissions.chargingCardPermissions.editChargingCard ? formValues.userId : undefined,
      expiration_date: permissions.chargingCardPermissions.editChargingCard && formValues.expirationDate ? formValues.expirationDate : undefined,
    }

    editChargingCard({
      payload,
      ocpiTokenId: chargingCard.ocpiTokenId,
      installationId: Number(chargingCard.installationId),
    })
  }

  const cardLabel = getChargingCardLabel({
    rfid: chargingCard.tokenId,
    cardName: chargingCard.cardName,
    t,
  })

  const selectUsersOptions = useMemo(
    () =>
      users.map(user => ({
        label: user.name,
        value: user.userId,
      })),
    [users]
  )

  /**
   * Status field does not affect the "Status" RadioGroup in isolation, but the whole dialog(disables the dialog action
   * button and shows an info text when the card is in "Inactive" state).
   *
   * For this reason, we must watch it at the top of the function body. However, after toggled once from "Inactive" to any
   * other state, additional renders do not occur.
   */
  const chargingCardStatusFormValue = useWatch({
    control,
    name: 'status',
  })

  const chargingCardExpirationDateFormValue = useWatch({
    control,
    name: 'expirationDate',
  })

  const isCardInactive = chargingCardStatusFormValue === CardStatus.inactive
  const isCardExpired = chargingCardExpirationDateFormValue && isPast(new Date(chargingCardExpirationDateFormValue))

  return (
    <Dialog
      maxWidth="sm"
      title={t('emobility.charging-cards.manage-charging-card-details-dialog-title', {
        cardLabel,
      })}
      onClose={onClose}
      place={place}
      actions={{
        primary: {
          disabled: isCardInactive || !isDirty || !isValid || isLoading,
          isLoading,
          name: t('emobility.tariffs.save'),
          onClick: handleSubmit,
        },
        secondary: {
          name: t('emobility.tariffs.cancel'),
          disabled: isLoading,
        },
      }}
    >
      {permissions.chargingCardPermissions.editChargingCardStatus && (
        <>
          <Typography variant="body2" component="p" mt={4}>
            {t('emobility.chargingCards.status.status')}
          </Typography>

          <Controller
            name="status"
            control={control}
            render={({ field: { value, onChange } }) => (
              <RadioGroup aria-label="charging-card-status" value={value} onChange={onChange} className={classes.radioButtons}>
                {isCardInactive && (
                  <FormControlLabel
                    value={CardStatus.inactive}
                    control={<Radio color="primary" />}
                    label={t('emobility.chargingCards.status.INACTIVE')}
                    disabled
                  />
                )}
                <FormControlLabel
                  value={CardStatus.active}
                  control={<Radio color="primary" />}
                  label={t('emobility.chargingCards.status.ACTIVE')}
                  disabled={Boolean(isCardExpired)}
                />
                <FormControlLabel
                  value={CardStatus.rejected}
                  control={<Radio color="primary" />}
                  label={t('emobility.chargingCards.status.DENIED')}
                  disabled={Boolean(isCardExpired)}
                />
                <FormControlLabel
                  value={CardStatus.expired}
                  control={<Radio color="primary" />}
                  label={t('emobility.chargingCards.status.EXPIRED')}
                  disabled={isCardInactive || !isCardExpired}
                />
              </RadioGroup>
            )}
          />
          {isCardInactive && <Typography className={classes.infoText}>{t('emobility.chargingCards.select-card-status')}</Typography>}
          {isCardExpired && !permissions.chargingCardPermissions.editChargingCard && (
            <Typography className={classes.infoText}>
              <Trans i18nKey={t('emobility.chargingCards.expired-card-status', { cardLabel })} components={{ strong: <strong /> }} />
            </Typography>
          )}
        </>
      )}

      {permissions.chargingCardPermissions.editChargingCard && (
        <Stack direction="column" spacing={4} mt={4}>
          <Controller
            name="cardName"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextField
                value={value ?? undefined}
                onChange={onChange}
                placeholder={t('emobility.chargingCards.edit.card-name')}
                label={t('emobility.chargingCards.edit.card-name')}
                helperText={errors.cardName?.message}
                clearable={false}
                disabled={isCardInactive}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <TextField
                  value={value ?? undefined}
                  onChange={onChange}
                  placeholder={t('emobility.chargingCards.edit.details')}
                  label={t('emobility.chargingCards.edit.details')}
                  helperText={errors.description?.message}
                  clearable={false}
                  disabled={isCardInactive}
                />
              )
            }}
          />

          <Controller
            name="expirationDate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DatePicker
                value={moment(value)}
                onChange={m => {
                  /**
                   * Why this onChange logic looks more complex than expected:
                   *
                   * - The DatePicker intentionally works with `moment` values (not raw text input),
                   *   which is convenient for most cases. However, when the user types an incomplete
                   *   or malformed date, the component still emits a `moment` instance.
                   *
                   * - In those cases, `m.isValid()` becomes the only reliable way to detect that
                   *   the input is not a valid date. We do NOT receive the original string, so we
                   *   cannot validate it directly.
                   *
                   * - Without this handling, invalid or partial inputs (e.g. "11/11/111") would
                   *   either be treated as valid dates or silently cleared by the DatePicker,
                   *   leading to inconsistent form state and validation issues.
                   *
                   * - To keep form behavior predictable, we map the result explicitly:
                   *   - `null` → user cleared the input
                   *   - invalid date → '' (treated as invalid in the form)
                   *   - valid date → ISO string (normalized to start/end of day)
                   *
                   * In short: this ensures that partial/invalid user input is not lost and is
                   * correctly reflected in form validation, despite the DatePicker not exposing
                   * raw input values.
                   */
                  let nextValue: string | null = null

                  if (m) {
                    nextValue = m.isValid() ? m.endOf('day').toISOString() : ''
                  }

                  const isSelectedDateInPast = moment() > moment(nextValue)

                  if (isSelectedDateInPast) {
                    onChange(nextValue)
                    trigger('expirationDate')
                    setValue('status', CardStatus.expired)
                    return
                  }

                  if (chargingCard.status === CardStatus.expired) {
                    setValue('status', CardStatus.active)
                  }

                  onChange(nextValue)
                  trigger('expirationDate') /** manually trigger cross-field validation for which react-hook-form optimizes renders */
                }}
                label={t('emobility.chargingCards.expiration-date')}
                helperText={errors.expirationDate?.message}
                minDate={moment(new Date())}
                disabled={isCardInactive}
              />
            )}
          />

          <Box className={classes.selectInputWrapper}>
            <Typography variant="body2" component="p" className={classes.title}>
              {t('emobility.chargingCards.edit.assignUser')}
            </Typography>
            <Controller
              name="userId"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Select
                    label={t('emobility.chargingCards.edit.user')}
                    value={value}
                    options={selectUsersOptions}
                    onChange={value => {
                      onChange(value ?? null)
                    }}
                    isSearchable={true}
                    clearable
                    disabled={isCardInactive}
                  />
                )
              }}
            />
          </Box>
        </Stack>
      )}
    </Dialog>
  )
}
